import React, { FC, useEffect, useMemo } from "react";

import { currentFolderRootBreadcrumbContext } from "@keepeek/commons";
import {
  KImageWidgetAnimationType,
  KNewsWidget as KNewsWidgetComponent,
  KNewsWidgetButtonPosition,
  KNewsWidgetChipPosition,
  KNewsWidgetChipStyle,
  KNewsWidgetDisplayMode,
  KNewsWidgetIconPosition,
  KNewsWidgetImagePosition,
  KNewsWidgetTextBackgroundType,
  KNewsWidgetVerticalTextAlignType,
  useThemeColors,
  KNewsWidgetProps as KNewsWidgetPropsComponent,
  mosaicCurrentSelectedElement,
  useBreadcrumb,
  DefaultIcon,
} from "@keepeek/refront-components";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import sanitizeHtml from "sanitize-html";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { FolderBrowse } from "../../../models/configuration/components/folderBrowse";
import {
  LinkType,
  Resource,
  TranslationSchema,
} from "../../../models/configuration/components/kImageWidget";
import {
  Button,
  Chip,
  DisplayMode,
  Image,
  ImagePosition,
  URL,
} from "../../../models/configuration/components/kNewsWidget";
import {
  BackgroundTextEffect,
  VerticalTextAlign,
} from "../../../models/configuration/components/mosaic";
import { useActiveFiltersFromURL } from "../../../providers/businessFilter/hooks/useActiveFiltersFromURL";
import { useConfiguration } from "../../../providers/config/hooks";
import { useInitConfig } from "../../../providers/config/hooks/init";
import { useImageOptim } from "../../../providers/config/hooks/useImageOptim";
import { useCurrentLocale } from "../../../providers/i18n/hooks";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";
import KNewsWidgetContent from "./KNewsWidgetContent";

export type KNewsWidgetProps = {
  widgetId: string;
  fixedHeight?: boolean;
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: string;
  url?: URL;
  button?: Button;
  chip?: Chip;
  content?: TranslationSchema[];
  image?: Image;
  imagePosition?: ImagePosition;
  imageWidthAdaptation?: boolean;
  marginTop?: string;
  marginBottom?: string;
  displayMode?: DisplayMode;
  backgroundTextEffect?: BackgroundTextEffect;
  backgroundTextColor?: string;
  verticalTextAlign?: VerticalTextAlign;
  padding?: string;
} & KWidgetProps;

const KNewsWidget: FC<React.PropsWithChildren<KNewsWidgetProps>> = function ({
  fixedHeight,
  backgroundColor,
  borderColor,
  borderRadius,
  url,
  button,
  chip,
  content,
  widgetId,
  image,
  imagePosition,
  imageWidthAdaptation,
  type,
  marginBottom,
  marginTop,
  displayMode,
  backgroundTextColor,
  backgroundTextEffect,
  verticalTextAlign,
  padding,
}) {
  const currentLocale = useCurrentLocale();
  const router = useRouter();
  const { convertColor } = useThemeColors();

  const { setWidgetState, widgetState } = useWidgetState({
    id: widgetId,
    type,
  });
  const { showSubFolderLink = false } = useConfiguration<FolderBrowse>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_FOLDER_BROWSE,
  ) ?? { showSubFolderLink: undefined };
  const { initConfig } = useInitConfig();
  const { setActiveFilters } = useActiveFiltersFromURL();
  const sentryCallback = ({ category, message }) => Sentry.addBreadcrumb({ category, message });

  const currentSelectedId = useRecoilValue(mosaicCurrentSelectedElement(widgetId));
  const setCurrentFolderId = useSetRecoilState(currentFolderRootBreadcrumbContext);

  const { search } = useBreadcrumb({
    initFolderId: currentSelectedId,
    initFolderRootId: currentSelectedId,
    initialShowFolders: false,
    key: `useNewsColumnWidget-${widgetId}`,
    resetSearch: true,
    clientIp: initConfig?.clientIp,
    showSubFolderLink,
    setActiveFilters,
    sentryCallback,
  });

  const translatedButton = {
    ...button,
    backgroundColor: convertColor(button?.backgroundColor),
    borderColor: convertColor(button?.borderColor),
    hoverColor: convertColor(button?.hoverColor),
    labelColor: convertColor(button?.labelColor),
    label: button?.label?.find((u) => u.language === currentLocale)?.value || "",
    onClick: () => {
      if (url) {
        if (url.linkType === LinkType.Folder) {
          setCurrentFolderId(undefined); // Remove current folderId to get folder id from url
          search({
            folderId: url.folderId ?? -1,
            extraParams: { fc: url.folderId },
          });
        } else if (url.href && url.href !== "about:blank") {
          if (url.targetBlank) {
            window.open(url.href, "_blank");
          } else {
            router.push(url.href);
          }
        }
      }
    },
    icon: button?.icon ? <DefaultIcon className={button?.icon} /> : undefined,
    iconPosition: button?.iconPosition
      ? (button?.iconPosition as unknown as KNewsWidgetIconPosition)
      : undefined,
    position: button?.iconPosition
      ? (button.position as unknown as KNewsWidgetButtonPosition)
      : undefined,
  };

  const translatedChip = {
    ...chip,
    color: convertColor(chip?.color),
    labelColor: convertColor(chip?.labelColor),
    label: chip?.label?.find((u) => u.language === currentLocale)?.value || "",
    position: chip?.position ? (chip.position as unknown as KNewsWidgetChipPosition) : undefined,
    style: chip?.style ? (chip.style as unknown as KNewsWidgetChipStyle) : undefined,
  };

  let imageUrl = image && image.url?.find((u) => u.language === currentLocale)?.value;
  const resource: Resource | undefined = image?.url?.find(
    (u) => u.language === currentLocale,
  )?.resource;
  if (resource && resource.url) {
    imageUrl = resource.url;
  }

  const { loading } = useImageOptim();

  const translatedImage: KNewsWidgetPropsComponent["image"] = useMemo(
    () => ({
      url: imageUrl,
      alt:
        (image &&
          sanitizeHtml(image.alt?.find((a) => a.language === currentLocale)?.value || "")) ||
        (image && sanitizeHtml(image.alt?.[0]?.value || "")),
      minHeight: image && image.minHeight,
      borderRadius: image && image.borderRadius,
      animationType:
        (image &&
          image.animation &&
          image.animation.animationType &&
          (image.animation.animationType as unknown as KImageWidgetAnimationType)) ||
        KImageWidgetAnimationType.NoAnimation,
    }),
    [image, imageUrl, currentLocale],
  );

  const translatedContent = content?.find((c) => c.language === currentLocale)?.value;

  useEffect(() => {
    // Do not render if we do not have at least:
    if (
      !(
        translatedContent ||
        !!translatedImage.url ||
        !!translatedButton.label ||
        !!button?.icon ||
        !!translatedChip.label
      )
    ) {
      setWidgetState({ state: WidgetUIState.NULL });
    } else {
      setWidgetState({ state: WidgetUIState.RENDERED });
    }
  }, [
    translatedImage,
    setWidgetState,
    translatedContent,
    translatedButton.label,
    translatedChip.label,
    button?.icon,
  ]);
  if (widgetState.state === WidgetUIState.RENDERED) {
    return (
      <>
        {!loading && (
          <KNewsWidgetComponent
            displayMode={displayMode as unknown as KNewsWidgetDisplayMode}
            fixedHeight={fixedHeight}
            backgroundTextColor={convertColor(backgroundTextColor)}
            backgroundTextEffect={backgroundTextEffect as unknown as KNewsWidgetTextBackgroundType}
            verticalTextAlign={verticalTextAlign as unknown as KNewsWidgetVerticalTextAlignType}
            backgroundColor={convertColor(backgroundColor)}
            borderColor={convertColor(borderColor)}
            borderRadius={borderRadius}
            button={translatedButton}
            chip={translatedChip}
            content={<KNewsWidgetContent content={translatedContent} />}
            id={widgetId}
            image={translatedImage}
            imagePosition={
              imagePosition ? (imagePosition as unknown as KNewsWidgetImagePosition) : undefined
            }
            imageWidthAdaptation={imageWidthAdaptation ? imageWidthAdaptation : false}
            marginTop={marginTop}
            marginBottom={marginBottom}
            padding={padding}
          />
        )}
      </>
    );
  }
  return null;
};

export default KNewsWidget;
